import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { buildClassName } from '@bluebeam/build-class-name';
import { useDataAttributes } from '@bluebeam/react-hooks';
import { GeneralIcons } from '@bluebeam/react-skyline-art';
import { useRect } from '@reach/rect';
import { getDocumentDimensions } from '@reach/utils';
import { useTooltip } from '@react-aria/tooltip';
import { mergeProps } from '@react-aria/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
const TOOLTIP_MAX_WIDTH = 450;
const TRIANGLE_BOX_WIDTH = 12;
const TRIANGLE_OFFSET = 2;
const getPlacementStyles = (placement, contentHeight, contentWidth, triggerRect) => {
    const offset = TRIANGLE_OFFSET + TRIANGLE_BOX_WIDTH;
    const { width: windowWidth, height: windowHeight } = getDocumentDimensions();
    if (placement.toLowerCase() === 'top' || placement.toLowerCase() === 'bottom') {
        const triggerCenterHorizontal = triggerRect.left + triggerRect.width / 2;
        const minimumLeft = 0;
        const maximumLeft = windowWidth - contentWidth;
        const contentLeft = Math.min(Math.max(triggerCenterHorizontal - contentWidth / 2, minimumLeft), maximumLeft);
        const triangleLeft = triggerRect.left + triggerRect.width / 2 - TRIANGLE_BOX_WIDTH / 2;
        let contentTop;
        let triangleTop;
        let rotation;
        if (placement.toLowerCase() === 'bottom') {
            const hasBottomCollision = windowHeight < triggerRect.bottom + contentHeight + offset;
            contentTop = hasBottomCollision
                ? triggerRect.top - contentHeight - offset
                : triggerRect.bottom + offset;
            triangleTop = hasBottomCollision ? triggerRect.top - offset : contentTop - TRIANGLE_BOX_WIDTH;
            rotation = hasBottomCollision ? 180 : 0;
        }
        else {
            const hasTopCollision = triggerRect.top - contentHeight - offset < 0;
            contentTop = hasTopCollision
                ? triggerRect.bottom + offset
                : triggerRect.top - contentHeight - offset;
            triangleTop = hasTopCollision ? contentTop - TRIANGLE_BOX_WIDTH : triggerRect.top - offset;
            rotation = hasTopCollision ? 0 : 180;
        }
        return {
            contentLeft,
            contentTop,
            triangleLeft,
            triangleTop,
            rotation,
        };
    }
    const hasRightCollision = windowWidth < triggerRect.right + contentWidth + offset;
    const triggerCenterVertical = triggerRect.top + triggerRect.height / 2;
    const triangleTop = triggerCenterVertical - TRIANGLE_BOX_WIDTH / 2;
    const contentTop = triggerCenterVertical - contentHeight / 2;
    const triangleLeft = hasRightCollision
        ? triggerRect.left - offset - TRIANGLE_OFFSET
        : triggerRect.right + TRIANGLE_OFFSET + window.scrollX;
    const contentLeft = hasRightCollision
        ? triangleLeft - contentWidth
        : triangleLeft + TRIANGLE_BOX_WIDTH;
    const rotation = hasRightCollision ? 90 : -90;
    return {
        contentLeft,
        contentTop,
        triangleLeft,
        triangleTop,
        rotation,
    };
};
const TooltipContent = ({ state, isInfo, triggerRect, placement, className, zIndex, ...props }) => {
    const dataAttributes = useDataAttributes(props);
    const contentRef = useRef(null);
    const contentRect = useRect(contentRef, { observe: Boolean(contentRef.current) });
    const contentWidth = contentRect?.width ?? 0;
    const contentHeight = contentRect?.height ?? 0;
    const { tooltipProps } = useTooltip({}, state);
    const { contentLeft, contentTop, triangleLeft, triangleTop, rotation } = getPlacementStyles(placement, contentHeight, contentWidth, triggerRect);
    const { width: windowWidth } = getDocumentDimensions();
    const maxWidth = Math.min(TOOLTIP_MAX_WIDTH, placement.toLowerCase() === 'right' ? windowWidth - contentLeft : windowWidth);
    const variants = {
        hidden: { opacity: 0, transition: { duration: 0.25 } },
        visible: { opacity: 1, transition: { duration: 0.5 } },
    };
    const [offset, setOffset] = useState(null);
    useEffect(() => {
        function getTransformAncestor(el) {
            if (el.parentElement) {
                const style = window.getComputedStyle(el.parentElement);
                if (style.transform === 'none') {
                    return getTransformAncestor(el.parentElement);
                }
                else {
                    return el.parentElement;
                }
            }
            else {
                return el;
            }
        }
        function updateOffset() {
            if (contentRef.current) {
                const rect = getTransformAncestor(contentRef.current).getBoundingClientRect();
                setOffset({
                    x: rect.x + document.documentElement.scrollLeft,
                    y: rect.y + document.documentElement.scrollTop,
                });
            }
            else {
                setOffset(null);
            }
        }
        updateOffset();
        window.addEventListener('resize', updateOffset);
        return () => {
            window.removeEventListener('resize', updateOffset);
        };
    }, [state.isOpen]);
    return (_jsx(AnimatePresence, { children: state.isOpen && (_jsxs(motion.span, { animate: "visible", className: buildClassName('fixed'), exit: "hidden", initial: "hidden", style: { zIndex }, variants: variants, ...dataAttributes, children: [_jsxs("span", { className: buildClassName('fixed flex items-center gap-2', 'skyline-text-body-2 leading-5 tracking-[0.01em] text-white', 'rounded border-none', 'shadow-[0_2px_8px_rgba(0, 47, 92, 0.16)] bg-black', 'px-4 py-2', 'w-max', className), ref: contentRef, style: offset
                        ? {
                            left: `${contentLeft - offset.x}px`,
                            top: `${contentTop - offset.y}px`,
                            maxWidth: `${maxWidth}px`,
                        }
                        : { display: 'none' }, ...mergeProps(props, tooltipProps), children: [isInfo && (_jsx("span", { className: "ml-1 shrink-0 leading-[0]", children: _jsx(GeneralIcons.InfoIcon, { size: "24px" }) })), props.children] }), _jsx("span", { style: offset
                        ? {
                            position: 'fixed',
                            left: `${triangleLeft - offset.x}px`,
                            top: `${triangleTop - offset.y}px`,
                            transform: rotation ? `rotate(${rotation}deg)` : undefined,
                            border: `${TRIANGLE_BOX_WIDTH / 2}px solid transparent`,
                            borderBottom: `${TRIANGLE_BOX_WIDTH / 2}px solid #373D42`,
                        }
                        : { display: 'none' } })] })) }));
};
export default TooltipContent;
//# sourceMappingURL=TooltipContent.js.map